import React, { useState } from 'react'
import Reason from '../Reason'
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Icon,
    Table,
    TableCaption,
    TableContainer,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
    useToast,
    Text
} from '@chakra-ui/react'
import Warehouse from '../../../../models/warehouse'
import { useGet } from '../../../../hooks/useHttp'
import SelectWarehouse from '../SelectWarehouse'
import FieldSearchProductionItem from '../FieldSearchProductionItem'
import useUpdateStock from '../../hooks/useUpdateStock'
import Close from '../../../../components/Icon/Close'
import colors from '../../../../config/theme/colors'
import CustomNumberInput from '../CustomNumberInput'
import { TYPES_SEARCH_PRODUCTION_ITEM } from '../../utils/constants'
import { handleSelectSearchProductionItem } from '../../utils/productionItem'

interface Props {
    onClose: () => void
}

export default function AddStock({ onClose }: Props) {
    const [helper, setHelper] = useState<string>('')
    const [form, setForm] = useState({
        warehouse_id: '',
        reason: ''
    })
    const [productionItems, setProductionsItems] = useState<any[]>([])
    const [errorItems, setErrorItems] = useState<any[]>([])
    const [typeSearch, setTypeSearch] = useState<string>(
        TYPES_SEARCH_PRODUCTION_ITEM.NAME
    )

    const { data } = useGet<Warehouse[]>('/api/warehouses', [])

    const toast = useToast()

    const { submit, isLoading } = useUpdateStock({
        handleFinish: () => {
            toast({
                status: 'success',
                title: 'Se agrego el stock correctamente',
                position: 'top'
            })
            onClose()
        },
        handleError: () => {
            toast({
                status: 'error',
                title: 'Ocurrio un error, al agregar stock',
                position: 'top'
            })
        }
    })

    const validData = () => {
        let isValid = true
        if (!form.warehouse_id) {
            setHelper('Debe seleccionar un almacén')
            isValid = false
        }

        if (productionItems.length === 0) {
            isValid = false
        } else {
            const errorProductionItems = []
            for (const productionItem of productionItems) {
                let errors = {}
                if (Number(productionItem.quantity) <= 0) {
                    errors = {
                        ...errors,
                        quantity: 'La cantidad debe ser mayor a 0'
                    }
                    isValid = false
                } else {
                    errors = {
                        ...errors,
                        quantity: ''
                    }
                }
                if (Number(productionItem.cost) <= 0) {
                    errors = {
                        ...errors,
                        cost: 'El costo debe ser mayor a 0'
                    }

                    isValid = false
                } else {
                    errors = {
                        ...errors,
                        cost: ''
                    }
                }
                errorProductionItems.push({
                    ...errors
                })
            }
            if (errorProductionItems.length > 0) {
                setErrorItems(errorProductionItems)
            }
        }

        return isValid
    }

    const handleSubmit = async () => {
        if (!validData()) {
            return
        }

        const production_items = []
        for (const dataproductionItem of productionItems) {
            production_items.push({
                production_item_id: dataproductionItem.production_item.id,
                quantity: dataproductionItem.quantity,
                unit_cost: dataproductionItem.cost
            })
        }

        await submit({
            type_change: true,
            warehouse_id: Number(form.warehouse_id),
            reason: form.reason,
            production_items
        })
    }

    const handleChange = (value: string, index: number, name: string) => {
        productionItems[index][name] = value
        setProductionsItems([...productionItems])
        if (errorItems[index]) {
            errorItems[index][name] = ''
            setErrorItems([...errorItems])
        }
    }

    const handleDelete = (index: number) => {
        const newProductionItems = []
        for (let i = 0; i < productionItems.length; i++) {
            if (i !== index) {
                newProductionItems.push(productionItems[i])
            }
        }
        setProductionsItems([...newProductionItems])

        if (index < errorItems.length && errorItems.length > 0) {
            const newErrorItems = []
            for (let i = 0; i < errorItems.length; i++) {
                if (i !== index) {
                    newErrorItems.push(errorItems[i])
                }
            }
            setErrorItems([...newErrorItems])
        }
    }

    return (
        <>
            <Box
                display={'flex'}
                flexDir={{
                    base: 'column',
                    md: 'row'
                }}
                gap={4}
                mt={4}
            >
                <Box
                    w={{
                        base: 'full',
                        md: '70%'
                    }}
                    rounded={'lg'}
                    bg="white"
                >
                    <FormControl
                        p={4}
                        borderBottom={'1px solid'}
                        borderColor={'gray.200'}
                    >
                        <SelectWarehouse
                            value={form.warehouse_id}
                            handleChange={e => {
                                setForm({
                                    ...form,
                                    warehouse_id: e.target.value
                                })
                                setHelper('')
                            }}
                            helper={helper}
                            options={data.result}
                        />
                    </FormControl>
                    <Box p={4} mt={4}>
                        <FormControl
                            mb={6}
                            w={{
                                base: 'full',
                                xl: '63.5%'
                            }}
                        >
                            <FormLabel>Buscar Articulo</FormLabel>
                            <FieldSearchProductionItem
                                onSelect={_productionItem => {
                                    handleSelectSearchProductionItem({
                                        productionItem: _productionItem,
                                        productionItems,
                                        setProductionsItems,
                                        typeSearch
                                    })
                                }}
                                warehouse_id={form.warehouse_id}
                                validWarehouse={() => {
                                    setHelper('Debe seleccionar un almacén')
                                }}
                                typeSearch={typeSearch}
                                setTypeSearch={setTypeSearch}
                            />
                        </FormControl>
                        <TableContainer>
                            <Table size="sm">
                                <Thead>
                                    <Tr>
                                        <Th>Articulo</Th>
                                        <Th>Stock</Th>
                                        <Th>Unidad</Th>
                                        <Th>Costo unitario</Th>
                                        <Th>Cantidad a agregar</Th>
                                        <Th></Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {productionItems.map(
                                        (itemMovement, index) => (
                                            <Tr p={2} key={itemMovement.id}>
                                                <Td>
                                                    {
                                                        itemMovement
                                                            .production_item
                                                            ?.name
                                                    }
                                                </Td>
                                                <Td>{itemMovement.stock}</Td>
                                                <Td>
                                                    {
                                                        itemMovement
                                                            ?.production_item
                                                            ?.magnitude?.name
                                                    }
                                                </Td>
                                                <Td>
                                                    <CustomNumberInput
                                                        valueForm={
                                                            itemMovement.cost ??
                                                            ''
                                                        }
                                                        handleChange={
                                                            handleChange
                                                        }
                                                        name="cost"
                                                        placeholder="Ingresar costo"
                                                        precision={2}
                                                        index={index}
                                                    />
                                                    <Text
                                                        fontSize={'xs'}
                                                        color="red.500"
                                                    >
                                                        {
                                                            errorItems?.[index]
                                                                ?.cost
                                                        }
                                                    </Text>
                                                </Td>
                                                <Td>
                                                    <CustomNumberInput
                                                        valueForm={
                                                            itemMovement.quantity
                                                        }
                                                        handleChange={
                                                            handleChange
                                                        }
                                                        name="quantity"
                                                        placeholder="Ingresar cant."
                                                        precision={2}
                                                        index={index}
                                                    />
                                                    <Text
                                                        fontSize={'xs'}
                                                        color="red.500"
                                                    >
                                                        {
                                                            errorItems?.[index]
                                                                ?.quantity
                                                        }
                                                    </Text>
                                                </Td>

                                                <Td>
                                                    <Icon
                                                        onClick={() =>
                                                            handleDelete(index)
                                                        }
                                                        cursor="pointer"
                                                        fill={colors.error}
                                                        as={Close}
                                                    />
                                                </Td>
                                            </Tr>
                                        )
                                    )}
                                </Tbody>
                                {productionItems.length === 0 && (
                                    <TableCaption>
                                        No existen articulos
                                    </TableCaption>
                                )}
                            </Table>
                        </TableContainer>
                    </Box>
                </Box>
                <Box
                    w={{
                        base: 'full',
                        md: '30%'
                    }}
                >
                    <Reason
                        handleChange={e => {
                            setForm({
                                ...form,
                                reason: e.target.value
                            })
                        }}
                    />
                </Box>
            </Box>

            <Box mt={3} display={'flex'} justifyContent={'end'}>
                <Button
                    variant="alo-gray:lg-md"
                    bg="white"
                    color="alo-gray"
                    onClick={onClose}
                    disabled={isLoading}
                    type="button"
                >
                    Cancelar
                </Button>

                <Button
                    variant="alo-green:lg-md"
                    ml={3}
                    type="submit"
                    isLoading={isLoading}
                    onClick={handleSubmit}
                >
                    Guardar
                </Button>
            </Box>
        </>
    )
}
